import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { useSettingsStore } from './settingsStore'
import { useFeatureFlagsStore } from './featureFlagsStore'
import { useConfigStore } from './configState'
import {
  migratePrintStateV0toV1,
  resolvePrintPlan as resolvePlanPure,
  type CategoryClaim,
  type PersistedPrintStateV0,
  type PrinterRouting,
  type PrintPlan,
  type RoutingSlot,
  migratePrintStateV1toV2,
  type SlotId
} from '@renderer/utils/printRouting'

export interface PrintSettings {
  orientation: 'landscape' | 'portrait'
  pageSize: {
    height: number
    width: number
  }
  margins?: {
    marginType: 'default' | 'none' | 'printableArea' | 'custom'
    top?: number
    bottom?: number
    left?: number
    right?: number
  }
  dpi?: {
    horizontal: number
    vertical: number
  }
  /**
   * Scale factor as a percentage (10-200)
   */
  scaleFactor?: number
  printBackground?: boolean
  printResolutionScale?: number
  /**
   * Print position shift for fine-tuning alignment (in cm)
   * Adjusts @page margins independently from margin settings
   */
  printPositionShift?: {
    top?: number // in cm
    bottom?: number // in cm
    left?: number // in cm
    right?: number // in cm
  }
}

/**
 * How a print job is delivered to the printer.
 * - electron: Electron webContents.print() (default)
 * - sumatra: PDF generated then sent via pdf-to-printer (a.k.a. "printer driver defaults")
 * - hotFolder: write the composed image file into a watched folder (e.g. DNP Hot Folder
 *   Print) so the printer software handles the physical size — required for DNP DS620A
 *   panoramic (6x14 / 6x20) printing.
 */
export type PrintMethod = 'electron' | 'sumatra' | 'hotFolder'

/**
 * Configuration for the hot-folder print method.
 * The folder is a directory the printer software (e.g. DNP HFP) monitors; each
 * watched folder maps to one output size. We simply drop image files into it.
 */
export interface HotFolderConfig {
  /** Absolute path to the watched folder to drop image files into. */
  folderPath: string
}

// Store for custom settings memory
interface CustomSettingsMemory {
  orientation: 'landscape' | 'portrait'
  pageSize: {
    height: number
    width: number
  }
  dpi: {
    horizontal: number
    vertical: number
  }
}

interface SecondaryPrinterConfig {
  printerName: string
  settings: PrintSettings
  /** @deprecated Mirrored into routing.frameIds by the v1 migration; still
   * written so a downgraded app keeps exact previous behaviour. */
  frameIds: string[]
  /** @deprecated Read only by the legacy parallel branch in view/Select.tsx
   * until Phase 3 retires it. */
  purpose?: 'special-frames' | 'parallel'
  /** What this slot claims — see utils/printRouting. */
  routing?: PrinterRouting
}

interface TertiaryPrinterConfig {
  printerName: string
  settings: PrintSettings
  /** @deprecated see SecondaryPrinterConfig.frameIds */
  frameIds: string[]
  /** @deprecated see SecondaryPrinterConfig.purpose */
  purpose?: 'special-frames' | 'parallel'
  /** What this slot claims — see utils/printRouting. */
  routing?: PrinterRouting
}

/** One printer installed on a remote booth PC, as advertised by that booth. */
export interface RemotePrinterEntry {
  name: string
  /** True for the printer the booth itself prints to. */
  isDefault?: boolean
  /** "4R · 4×6 · 102 × 152 mm", "80 mm thermal" — see utils/printerMedia. */
  mediaLabel?: string
  /** This printer's own calibration; page size differs wildly between stocks. */
  settings?: PrintSettings
}

interface RemotePrinterConfig {
  machineIdentifier: string // registry _id, identifies the MACHINE
  tunnelUrl: string
  printerName: string // physical printer on the remote machine (for the print payload)
  machineName?: string // booth PC hostname — the group heading in the picker
  projectName?: string // friendly label shown in the dropdown
  printSettings?: PrintSettings // settings registered by the remote booth
  /**
   * Every printer on that machine. The API always sends this (synthesising a
   * one-entry list for booths too old to publish one), so it is only empty when
   * the machine genuinely has no printers installed.
   */
  printers?: RemotePrinterEntry[]
  isAvailable: boolean
  /**
   * Set by `getSelectedRemotePrinter` when the operator's chosen printer is no
   * longer advertised by the booth — an unplugged printer, or a settings change
   * on the PC. Callers MUST refuse to print rather than fall back: silently
   * routing a karcis to a 4x6 dye-sub burns real media with no error anywhere.
   */
  printerMissing?: boolean
}

/** A remembered pick: which machine, and which printer on it. */
interface RemotePrinterSelection {
  machineIdentifier: string
  printerName?: string
}

interface PrintState {
  status: 'idle' | 'printing' | 'error' | 'success'
  error?: string
  currentJob?: {
    printer: string
    copies: number
    images: string[]
  }
  settings: PrintSettings
  selectedPrinter?: string
  /**
   * Android only: RAW/JetDirect address of the printer (port 9100). Android's print
   * framework shows a dialog unless we hand the bridge an IP, and a booth nobody is
   * standing at cannot tap through a dialog. Empty means "use the system dialog".
   */
  androidPrinterIp?: string
  isPrintingEnabled: boolean
  useCSharpPrinting: boolean
  useSumatraPDF: boolean
  secondaryUseSumatraPDF: boolean
  tertiaryUseSumatraPDF: boolean
  // Print method per printer slot. Falls back to useSumatraPDF when unset (see getPrintMethod).
  printMethod?: PrintMethod
  secondaryPrintMethod?: PrintMethod
  tertiaryPrintMethod?: PrintMethod
  // Hot-folder config per printer slot (only used when the slot's method is 'hotFolder').
  hotFolderConfig?: HotFolderConfig
  secondaryHotFolderConfig?: HotFolderConfig
  tertiaryHotFolderConfig?: HotFolderConfig
  secondaryPrinter?: SecondaryPrinterConfig
  tertiaryPrinter?: TertiaryPrinterConfig
  customSettingsMemory?: CustomSettingsMemory
  secondaryCustomSettingsMemory?: CustomSettingsMemory
  tertiaryCustomSettingsMemory?: CustomSettingsMemory
  /** @deprecated read once by the v1 migration; the cursor lives in roundRobinCursor. */
  lastParallelPrinterIndex?: number
  /** Routing claims for the PRIMARY slot. Migrated to allFrames catch-all. */
  defaultRouting?: PrinterRouting
  /** Round-robin cursor per sharer group (e.g. "secondary|tertiary"). */
  roundRobinCursor: Record<string, number>
  remotePrinters: RemotePrinterConfig[]
  selectedPrinterTarget: 'default' | 'remote'
  selectedRemotePrinterMachineId?: string
  /**
   * Which printer ON the selected machine. Undefined means "whichever the booth
   * calls its default", which is how every pre-multi-printer selection behaved.
   */
  selectedRemotePrinterName?: string
  /**
   * Remembered pick per project. The karcis flow wants the thermal printer and
   * the photostrip flow wants the dye-sub — on the same PC, at the same time.
   * A single global selection makes those two mutually exclusive, which is the
   * whole reason this map exists.
   */
  remoteSelectionByProject: Record<string, RemotePrinterSelection>
  /** Project currently on screen; the key `selectRemotePrinter` writes under. */
  activeProjectId?: string
  /**
   * Local print slot bound to a project (multi-project hub, plan §4.5). One
   * radio in Settings instead of ticking every koran frame on every booth PC.
   * Consulted by `resolvePrintPlan` after frame/category claims and before the
   * default catch-all; an empty map is byte-identical to today.
   */
  printerSlotByProject: Record<string, SlotId>
  // Per-session override of a remote booth's registered print settings (web detailed config).
  // undefined => defer to the booth's own calibration.
  remotePrintOverride?: PrintSettings
  setStatus: (status: PrintState['status'], error?: string) => void
  setCurrentJob: (job: PrintState['currentJob']) => void
  clearJob: () => void
  updateSettings: (settings: PrintSettings) => void
  setSelectedPrinter: (printer: string | undefined) => void
  setAndroidPrinterIp: (ip: string | undefined) => void
  togglePrinting: () => void
  setUseCSharpPrinting: (enabled: boolean) => void
  setUseSumatraPDF: (enabled: boolean) => void
  setSecondaryUseSumatraPDF: (enabled: boolean) => void
  setTertiaryUseSumatraPDF: (enabled: boolean) => void
  getPrintMethod: (printerType?: 'default' | 'secondary' | 'tertiary') => PrintMethod
  setPrintMethod: (
    method: PrintMethod,
    printerType?: 'default' | 'secondary' | 'tertiary'
  ) => void
  getHotFolderConfig: (
    printerType?: 'default' | 'secondary' | 'tertiary'
  ) => HotFolderConfig | undefined
  setHotFolderConfig: (
    config: HotFolderConfig | undefined,
    printerType?: 'default' | 'secondary' | 'tertiary'
  ) => void
  setSecondaryPrinter: (config: SecondaryPrinterConfig | undefined) => void
  setTertiaryPrinter: (config: TertiaryPrinterConfig | undefined) => void
  getPrinterForFrame: (frameId: string) => { printerName: string; settings: PrintSettings }
  setCustomSettingsMemory: (
    memory: CustomSettingsMemory,
    printerType?: 'default' | 'secondary' | 'tertiary'
  ) => void
  getCustomSettingsMemory: (
    printerType?: 'default' | 'secondary' | 'tertiary'
  ) => CustomSettingsMemory | undefined
  getNextParallelPrinter: () => { printerName: string; settings: PrintSettings } | null
  /** Full routing decision for a frame — claims, warnings, chosen target. */
  resolvePrintPlan: (frameId: string) => PrintPlan
  /** Replace the PRIMARY slot's claims (Phase 2 UI). */
  setDefaultRouting: (routing: PrinterRouting) => void
  /** Replace one slot's category claims, keeping its frameIds. */
  setSlotCategories: (
    printerType: 'default' | 'secondary' | 'tertiary',
    categories: CategoryClaim[]
  ) => void
  setRemotePrinters: (printers: RemotePrinterConfig[]) => void
  selectRemotePrinter: (machineIdentifier: string | undefined, printerName?: string) => void
  selectDefaultPrinter: () => void
  setActiveProject: (projectId: string | undefined) => void
  /** Bind (or, with `slot` undefined, unbind) a project's local print slot. */
  setPrinterSlotForProject: (projectId: string, slot?: SlotId) => void
  setRemotePrintOverride: (settings: PrintSettings | undefined) => void
  getSelectedRemotePrinter: () => RemotePrinterConfig | null
}

// Initial state for print store. selectedPrinter is not read from settingsStore here
// to avoid hydration race; it comes from this store's own persist rehydration ('print-settings').
// When setSelectedPrinter is called, we still sync to settingsStore so both stay in sync.
const getInitialState = (): Omit<
  PrintState,
  | 'setStatus'
  | 'setCurrentJob'
  | 'clearJob'
  | 'updateSettings'
  | 'setSelectedPrinter'
  | 'setAndroidPrinterIp'
  | 'togglePrinting'
  | 'setUseCSharpPrinting'
  | 'setUseSumatraPDF'
  | 'setSecondaryUseSumatraPDF'
  | 'setTertiaryUseSumatraPDF'
  | 'getPrintMethod'
  | 'setPrintMethod'
  | 'getHotFolderConfig'
  | 'setHotFolderConfig'
  | 'setSecondaryPrinter'
  | 'setTertiaryPrinter'
  | 'getPrinterForFrame'
  | 'setCustomSettingsMemory'
  | 'getCustomSettingsMemory'
  | 'getNextParallelPrinter'
  | 'resolvePrintPlan'
  | 'setDefaultRouting'
  | 'setSlotCategories'
  | 'setRemotePrinters'
  | 'selectRemotePrinter'
  | 'selectDefaultPrinter'
  | 'setActiveProject'
  | 'setPrinterSlotForProject'
  | 'setRemotePrintOverride'
  | 'getSelectedRemotePrinter'
> => {
  return {
    status: 'idle' as const,
    settings: {
      orientation: 'landscape' as const,
      pageSize: {
        height: 105000,
        width: 156100
      },
      scaleFactor: 100, // Default 100%
      printResolutionScale: 1 // Default to 1x scale
    },
    selectedPrinter: undefined,
    androidPrinterIp: undefined,
    isPrintingEnabled: true,
    useCSharpPrinting: false,
    useSumatraPDF: false,
    secondaryUseSumatraPDF: false,
    tertiaryUseSumatraPDF: false,
    remotePrinters: [],
    defaultRouting: { allFrames: true, frameIds: [], categories: [] },
    roundRobinCursor: {},
    selectedPrinterTarget: 'default',
    remoteSelectionByProject: {},
    printerSlotByProject: {},
    remotePrintOverride: undefined
  }
}

/**
 * Routing rule that picked the printer for the most recent `resolvePrintPlan`.
 * Print telemetry reads it right after the plan is resolved (printService has
 * the printer name, not the plan), so `routingMatch: 'project'` is observable
 * fleet-wide without threading the plan through thirteen call sites.
 */
let lastRoutingMatch: PrintPlan['target']['match'] | undefined
export function getLastRoutingMatch(): PrintPlan['target']['match'] | undefined {
  return lastRoutingMatch
}

export const usePrintStore = create<PrintState>()(
  persist(
    (set, get) => ({
      ...getInitialState(),
      setStatus: (status, error): void => void set({ status, error }),
      setCurrentJob: (currentJob): void => void set({ currentJob }),
      clearJob: (): void => void set({ currentJob: undefined, status: 'idle', error: undefined }),
      updateSettings: (settings): void => void set({ settings }),
      setSelectedPrinter: (printer): void => {
        set({ selectedPrinter: printer })
        // Sync with settings store
        useSettingsStore.getState().setSelectedPrinter(printer)
      },
      setAndroidPrinterIp: (ip): void =>
        void set({ androidPrinterIp: ip?.trim() ? ip.trim() : undefined }),
      togglePrinting: (): void =>
        void set((state) => ({ isPrintingEnabled: !state.isPrintingEnabled })),
      setUseCSharpPrinting: (enabled): void => void set({ useCSharpPrinting: enabled }),
      setUseSumatraPDF: (enabled): void => void set({ useSumatraPDF: enabled }),
      setSecondaryUseSumatraPDF: (enabled): void => void set({ secondaryUseSumatraPDF: enabled }),
      setTertiaryUseSumatraPDF: (enabled): void => void set({ tertiaryUseSumatraPDF: enabled }),
      getPrintMethod: (
        printerType: 'default' | 'secondary' | 'tertiary' = 'default'
      ): PrintMethod => {
        const state = get()
        // Prefer the explicit printMethod; fall back to the legacy useSumatraPDF boolean
        // so existing booth configs keep working without migration.
        if (printerType === 'secondary') {
          return state.secondaryPrintMethod ?? (state.secondaryUseSumatraPDF ? 'sumatra' : 'electron')
        }
        if (printerType === 'tertiary') {
          return state.tertiaryPrintMethod ?? (state.tertiaryUseSumatraPDF ? 'sumatra' : 'electron')
        }
        return state.printMethod ?? (state.useSumatraPDF ? 'sumatra' : 'electron')
      },
      setPrintMethod: (
        method: PrintMethod,
        printerType: 'default' | 'secondary' | 'tertiary' = 'default'
      ): void => {
        // Keep the legacy useSumatraPDF boolean in sync so any code still reading it
        // (and SumatraPDF-specific UI gating) behaves consistently.
        const sumatra = method === 'sumatra'
        if (printerType === 'secondary') {
          set({ secondaryPrintMethod: method, secondaryUseSumatraPDF: sumatra })
        } else if (printerType === 'tertiary') {
          set({ tertiaryPrintMethod: method, tertiaryUseSumatraPDF: sumatra })
        } else {
          set({ printMethod: method, useSumatraPDF: sumatra })
        }
      },
      getHotFolderConfig: (
        printerType: 'default' | 'secondary' | 'tertiary' = 'default'
      ): HotFolderConfig | undefined => {
        const state = get()
        if (printerType === 'secondary') return state.secondaryHotFolderConfig
        if (printerType === 'tertiary') return state.tertiaryHotFolderConfig
        return state.hotFolderConfig
      },
      setHotFolderConfig: (
        config: HotFolderConfig | undefined,
        printerType: 'default' | 'secondary' | 'tertiary' = 'default'
      ): void => {
        if (printerType === 'secondary') {
          set({ secondaryHotFolderConfig: config })
        } else if (printerType === 'tertiary') {
          set({ tertiaryHotFolderConfig: config })
        } else {
          set({ hotFolderConfig: config })
        }
      },
      setSecondaryPrinter: (config): void => {
        if (config) {
          const frameIds = [...(config.frameIds || [])]
          // Ensure frameIds is always a new array to prevent reference sharing
          // Default purpose to 'special-frames' for backward compatibility.
          // routing.frameIds MIRRORS the legacy frameIds on every write: the
          // Pilih Frame modal only knows about frameIds, and the resolver only
          // reads routing — without this mirror an edit would strand the claims
          // on their pre-edit value. Categories (Phase 2) are preserved.
          set({
            secondaryPrinter: {
              ...config,
              frameIds,
              purpose: config.purpose || 'special-frames',
              routing: {
                allFrames: false,
                // Preserve stored categories when the caller doesn't pass any:
                // legacy callers (Pilih Frame writeback) rebuild the slot with
                // no routing key, and must not wipe category claims.
                categories:
                  config.routing?.categories ?? get().secondaryPrinter?.routing?.categories ?? [],
                ...config.routing,
                frameIds
              }
            }
          })
        } else {
          set({ secondaryPrinter: undefined })
        }
      },
      setTertiaryPrinter: (config): void => {
        if (config) {
          const frameIds = [...(config.frameIds || [])]
          // Ensure frameIds is always a new array to prevent reference sharing
          // Default purpose to 'special-frames' for backward compatibility.
          // routing.frameIds MIRRORS the legacy frameIds on every write: the
          // Pilih Frame modal only knows about frameIds, and the resolver only
          // reads routing — without this mirror an edit would strand the claims
          // on their pre-edit value. Categories (Phase 2) are preserved.
          set({
            tertiaryPrinter: {
              ...config,
              frameIds,
              purpose: config.purpose || 'special-frames',
              routing: {
                allFrames: false,
                // Preserve stored categories when the caller doesn't pass any:
                // legacy callers (Pilih Frame writeback) rebuild the slot with
                // no routing key, and must not wipe category claims.
                categories:
                  config.routing?.categories ?? get().tertiaryPrinter?.routing?.categories ?? [],
                ...config.routing,
                frameIds
              }
            }
          })
        } else {
          set({ tertiaryPrinter: undefined })
        }
      },
      getPrinterForFrame: (frameId: string): { printerName: string; settings: PrintSettings } => {
        // Thin wrapper over the claims resolver. With the
        // printer_multi_claim_routing flag OFF (the default) the resolver is
        // proven byte-identical to the legacy tertiary→secondary→default
        // lookup by the equivalence suite in utils/__tests__/printRouting.test.ts.
        const { printerName, settings } = get().resolvePrintPlan(frameId).target
        return { printerName, settings }
      },
      resolvePrintPlan: (frameId: string): PrintPlan => {
        const state = get()
        const slots: RoutingSlot[] = [
          {
            slotId: 'default',
            printerName: state.selectedPrinter,
            settings: state.settings,
            routing: state.defaultRouting
          },
          {
            slotId: 'secondary',
            printerName: state.secondaryPrinter?.printerName,
            settings: state.secondaryPrinter?.settings ?? state.settings,
            routing: state.secondaryPrinter?.routing
          },
          {
            slotId: 'tertiary',
            printerName: state.tertiaryPrinter?.printerName,
            settings: state.tertiaryPrinter?.settings ?? state.settings,
            routing: state.tertiaryPrinter?.routing
          }
        ]
        // Live category catalog: in-memory, network-sourced, empty whenever
        // category mode is off — claims fall back to their snapshots then.
        const liveCategories = useConfigStore
          .getState()
          .frameCategories.map((c) => ({ name: c.name, ids: [...(c.ids || [])] }))
        const { plan, nextCursor } = resolvePlanPure(frameId, slots, liveCategories, {
          multiClaimRouting: useFeatureFlagsStore
            .getState()
            .getFlag('printer_multi_claim_routing'),
          cursor: state.roundRobinCursor,
          projectSlot: state.activeProjectId
            ? state.printerSlotByProject?.[state.activeProjectId]
            : undefined
        })
        if (nextCursor !== state.roundRobinCursor) {
          set({ roundRobinCursor: nextCursor })
        }
        lastRoutingMatch = plan.target.match
        return plan
      },
      setDefaultRouting: (routing): void => {
        set({
          defaultRouting: {
            allFrames: routing.allFrames,
            frameIds: [...routing.frameIds],
            categories: routing.categories.map((c) => ({
              ...c,
              frameIdsSnapshot: [...c.frameIdsSnapshot]
            }))
          }
        })
      },
      setSlotCategories: (printerType, categories): void => {
        const cloned = categories.map((c) => ({
          ...c,
          frameIdsSnapshot: [...c.frameIdsSnapshot]
        }))
        if (printerType === 'default') {
          const current = get().defaultRouting ?? {
            allFrames: true,
            frameIds: [],
            categories: []
          }
          set({ defaultRouting: { ...current, categories: cloned } })
          return
        }
        const key = printerType === 'secondary' ? 'secondaryPrinter' : 'tertiaryPrinter'
        const slot = get()[key]
        if (!slot) return
        set({
          [key]: {
            ...slot,
            routing: {
              allFrames: false,
              frameIds: [...slot.frameIds],
              ...slot.routing,
              categories: cloned
            }
          }
        } as Partial<PrintState>)
      },
      setCustomSettingsMemory: (
        memory: CustomSettingsMemory,
        printerType: 'default' | 'secondary' | 'tertiary' = 'default'
      ): void => {
        if (printerType === 'secondary') {
          set({ secondaryCustomSettingsMemory: memory })
        } else if (printerType === 'tertiary') {
          set({ tertiaryCustomSettingsMemory: memory })
        } else {
          set({ customSettingsMemory: memory })
        }
      },
      getCustomSettingsMemory: (
        printerType: 'default' | 'secondary' | 'tertiary' = 'default'
      ): CustomSettingsMemory | undefined => {
        const state = get()
        if (printerType === 'secondary') {
          return state.secondaryCustomSettingsMemory
        } else if (printerType === 'tertiary') {
          return state.tertiaryCustomSettingsMemory
        }
        return state.customSettingsMemory
      },
      getNextParallelPrinter: (): { printerName: string; settings: PrintSettings } | null => {
        const state = get()
        const enabledPrinters: Array<{ printerName: string; settings: PrintSettings }> = []

        // Always include default printer for parallel printing
        if (state.selectedPrinter) {
          enabledPrinters.push({
            printerName: state.selectedPrinter,
            settings: state.settings
          })
        }

        // Include secondary printer if purpose is 'parallel'
        if (state.secondaryPrinter?.printerName && state.secondaryPrinter.purpose === 'parallel') {
          enabledPrinters.push({
            printerName: state.secondaryPrinter.printerName,
            settings: state.secondaryPrinter.settings
          })
        }

        // Include tertiary printer if purpose is 'parallel'
        if (state.tertiaryPrinter?.printerName && state.tertiaryPrinter.purpose === 'parallel') {
          enabledPrinters.push({
            printerName: state.tertiaryPrinter.printerName,
            settings: state.tertiaryPrinter.settings
          })
        }

        if (enabledPrinters.length === 0) {
          return null
        }

        // Get or initialize last used index
        const lastIndex = state.lastParallelPrinterIndex ?? -1
        const nextIndex = (lastIndex + 1) % enabledPrinters.length

        // Update last used index
        set({ lastParallelPrinterIndex: nextIndex })

        return enabledPrinters[nextIndex]
      },
      setRemotePrinters: (printers): void => {
        set({ remotePrinters: printers })
      },
      selectRemotePrinter: (machineIdentifier, printerName): void => {
        const {
          activeProjectId,
          remoteSelectionByProject,
          selectedRemotePrinterMachineId,
          selectedRemotePrinterName
        } = get()

        if (!machineIdentifier) {
          set({
            selectedPrinterTarget: 'default',
            selectedRemotePrinterMachineId: undefined,
            selectedRemotePrinterName: undefined,
            // Drop the remembered pick too — otherwise reopening this project
            // would silently resurrect the remote printer the operator just
            // deliberately switched away from.
            ...(activeProjectId
              ? {
                  remoteSelectionByProject: Object.fromEntries(
                    Object.entries(remoteSelectionByProject).filter(
                      ([key]) => key !== activeProjectId
                    )
                  )
                }
              : {})
          })
          return
        }

        const switchedPrinter =
          selectedRemotePrinterMachineId !== machineIdentifier ||
          selectedRemotePrinterName !== printerName

        set({
          selectedPrinterTarget: 'remote',
          selectedRemotePrinterMachineId: machineIdentifier,
          selectedRemotePrinterName: printerName,
          // A manual override is calibration for ONE printer — thermal margins
          // applied to a 4x6 dye-sub would ruin every print. Drop it whenever
          // the target changes and fall back to the new printer's own settings.
          ...(switchedPrinter ? { remotePrintOverride: undefined } : {}),
          ...(activeProjectId
            ? {
                remoteSelectionByProject: {
                  ...remoteSelectionByProject,
                  [activeProjectId]: { machineIdentifier, printerName }
                }
              }
            : {})
        })
      },
      selectDefaultPrinter: (): void => {
        get().selectRemotePrinter(undefined)
      },
      /**
       * Point the store at a project and restore that project's remembered
       * printer. Called when the booth resolves which project it is running, so
       * a PC serving karcis and photostrips lands on the right printer for each
       * without the operator re-picking.
       */
      setActiveProject: (projectId): void => {
        const state = get()

        if (!projectId) {
          set({ activeProjectId: undefined })
          return
        }

        // Deliberately no "already active, nothing to do" short-circuit:
        // `activeProjectId` is persisted, so after a relaunch it rehydrates
        // already equal to the project about to mount. Bailing out there would
        // skip the restore below and leave the booth on whatever printer was
        // last selected globally. Re-running it is idempotent.
        const remembered = state.remoteSelectionByProject[projectId]
        if (!remembered) {
          // No pick recorded for this project. Leave whatever is selected alone
          // rather than clearing it — a booth that has only ever used one
          // printer has a valid global selection and no per-project entry yet.
          set({ activeProjectId: projectId })
          return
        }

        set({
          activeProjectId: projectId,
          selectedPrinterTarget: 'remote',
          selectedRemotePrinterMachineId: remembered.machineIdentifier,
          selectedRemotePrinterName: remembered.printerName
        })
      },
      setPrinterSlotForProject: (projectId, slot): void => {
        if (!projectId) return
        const current = get().printerSlotByProject ?? {}
        if (!slot) {
          if (!(projectId in current)) return
          const next = { ...current }
          delete next[projectId]
          set({ printerSlotByProject: next })
          return
        }
        set({ printerSlotByProject: { ...current, [projectId]: slot } })
      },
      setRemotePrintOverride: (settings): void => {
        set({ remotePrintOverride: settings })
      },
      getSelectedRemotePrinter: (): RemotePrinterConfig | null => {
        const state = get()
        if (state.selectedPrinterTarget !== 'remote' || !state.selectedRemotePrinterMachineId) {
          return null
        }

        const machine = state.remotePrinters.find(
          (p) => p.machineIdentifier === state.selectedRemotePrinterMachineId
        )
        if (!machine) return null

        const entries = machine.printers ?? []
        const wanted = state.selectedRemotePrinterName

        // No specific printer chosen — the pre-multi-printer behaviour. Use the
        // booth's own default, which is exactly what it used to register.
        if (!wanted) {
          const fallback = entries.find((e) => e.isDefault) ?? entries[0]
          return {
            ...machine,
            printerName: fallback?.name ?? machine.printerName,
            printSettings: fallback?.settings ?? machine.printSettings
          }
        }

        const entry = entries.find((e) => e.name === wanted)
        if (!entry) {
          // The chosen printer is gone (unplugged, renamed, or the booth was
          // reconfigured). Report it as missing and let the caller refuse. We
          // deliberately do NOT fall back to the machine's default: a karcis
          // routed to a 4x6 dye-sub costs real media and reports success.
          return { ...machine, printerName: wanted, printSettings: undefined, printerMissing: true }
        }

        return {
          ...machine,
          printerName: entry.name,
          printSettings: entry.settings ?? machine.printSettings
        }
      }
    }),
    {
      name: 'print-settings',
      // v0 (no recorded version) -> v1: derive routing claims from the legacy
      // frameIds/purpose fields. v1 -> v2: add the per-project slot binding
      // map. Both idempotent, and the legacy fields stay on disk untouched so
      // downgrading the app restores exact previous behaviour.
      version: 2,
      migrate: (persisted, _fromVersion): unknown =>
        migratePrintStateV1toV2(
          migratePrintStateV0toV1((persisted ?? {}) as PersistedPrintStateV0)
        ) as unknown,
      onRehydrateStorage: () => (state) => {
        if (state?.selectedPrinter !== undefined) {
          useSettingsStore.getState().setSelectedPrinter(state.selectedPrinter)
        }
      }
    }
  )
)
